/* eslint-disable unused-imports/no-unused-vars */
import { Dispatch, FormEvent, SetStateAction, useCallback, useContext, useEffect, useState } from "react";

import { InteractionObjectType, InteractionType, ProjectIssuanceConstants } from "../../constants";
import {
  PillData,
  ProjectIssuanceBNGPanelData,
  ProjectIssuanceCarbonPanelData,
  ProjectIssuanceSpecificProperty,
  ProjectIssuanceSpecificSchema,
  SelectData,
} from "../../models";
import { ProjectContext } from "../../route/shared/projects/project/ProjectContext";
import { addOrRemoveObjectInteractions } from "../../service/interaction";
import { createProjectIssuance, updateProjectIssuance } from "../../service/project/ProjectService.full";
import {
  getStandardSpecificSchemaDetails,
  searchInteractions,
  searchIssuanceTransactions,
  searchVerifiers,
} from "../../service/query";
import { ServiceError, Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { useIsLoadingWrapper } from "../../utils";
import { dataGridMapFilterCriteria, Toast } from "../../widget";
import { InteractionTag } from "../TagsPanel/models";

interface UseIssuancePanelProps {
  issuance?: ProjectIssuanceBNGPanelData | ProjectIssuanceCarbonPanelData;
  onClose: () => void;
  handleRefresh: () => void;
}

interface UseIssuancePanelReturnData {
  errors: ServiceError[] | undefined;
  issuanceSchema: ProjectIssuanceSpecificSchema | undefined;

  tagPills: PillData[];
  changedTagsExternal: InteractionTag[];
  setChangedTagsExternal: Dispatch<SetStateAction<InteractionTag[]>>;

  standardSpecificIssuanceData: ProjectIssuanceCarbonPanelData | ProjectIssuanceBNGPanelData;
  setStandardSpecificIssuanceData: Dispatch<
    SetStateAction<ProjectIssuanceCarbonPanelData | ProjectIssuanceBNGPanelData>
  >;

  showTagsPanel: boolean;
  setShowTagsPanel: Dispatch<SetStateAction<boolean>>;

  setRefreshTags: Dispatch<SetStateAction<boolean>>;

  isHandleSubmitLoading: boolean;
  handleClosePanel: () => void;
  handleSubmit: () => Promise<void>;
  handleSubmitForm: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  showIssuanceTransactionWarning: boolean;
  setShowIssuanceTransactionWarning: Dispatch<SetStateAction<boolean>>;

  verifierData: SelectData;
}

const BNG_SCHEMA_PROPERTY_ORDER = [
  "habitatType",
  "habitat",
  "habitatCondition",
  "habitatDistinctiveness",
  "createdOrEnhanced",
  "unitHa",
  "unitKm",
  "quantityType",
];

const CARBON_SCHEMA_PROPERTY_ORDER = ["verificationStatus", "vintageStartDate", "vintageEndDate", "quantityType"];

const reorderIssuanceSchemaProperties = (
  parsedSchema: ProjectIssuanceSpecificSchema
): Record<string, ProjectIssuanceSpecificProperty> => {
  const schemaPropertyEntries = Object.entries(parsedSchema.properties ?? {}) as [
    string,
    ProjectIssuanceSpecificProperty,
  ][];
  const schemaPropertyMap = new Map(schemaPropertyEntries);
  const unitMeasurement = schemaPropertyMap.get("unitMeasurement")?.const;

  let preferredOrder: string[] = [];

  if (unitMeasurement === "Biodiversity") {
    preferredOrder = BNG_SCHEMA_PROPERTY_ORDER;
  } else if (unitMeasurement === "Carbon") {
    preferredOrder = CARBON_SCHEMA_PROPERTY_ORDER;
  }

  if (preferredOrder.length === 0) {
    return parsedSchema.properties;
  }

  const preferredOrderSet = new Set(preferredOrder);
  const reorderedEntries: [string, ProjectIssuanceSpecificProperty][] = [];

  preferredOrder.forEach((fieldKey) => {
    const fieldDefinition = schemaPropertyMap.get(fieldKey);
    if (fieldDefinition) {
      reorderedEntries.push([fieldKey, fieldDefinition]);
    }
  });

  schemaPropertyEntries.forEach(([fieldKey, fieldDefinition]) => {
    if (!preferredOrderSet.has(fieldKey)) {
      reorderedEntries.push([fieldKey, fieldDefinition]);
    }
  });

  return Object.fromEntries(reorderedEntries);
};

export const useIssuancePanel = ({
  issuance,
  onClose,
  handleRefresh,
}: UseIssuancePanelProps): UseIssuancePanelReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const { projectDetails } = useContext(ProjectContext);
  const projectUnitMeasurement = projectDetails?.unitMeasurements?.[0];

  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const [showTagsPanel, setShowTagsPanel] = useState(false);

  const [issuanceSchema, setIssuanceSchema] = useState<ProjectIssuanceSpecificSchema>();
  const [standardSpecificIssuanceData, setStandardSpecificIssuanceData] = useState<
    ProjectIssuanceCarbonPanelData | ProjectIssuanceBNGPanelData
  >(
    projectUnitMeasurement === "Biodiversity"
      ? ProjectIssuanceConstants.DEFAULT_BNG_ISSUANCE
      : ProjectIssuanceConstants.DEFAULT_CARBON_ISSUANCE
  );

  const [tagPills, setTagPills] = useState<PillData[]>([]);
  const [changedTagsExternal, setChangedTagsExternal] = useState<InteractionTag[]>([]);
  const [refreshTags, setRefreshTags] = useState(false);

  const [showIssuanceTransactionWarning, setShowIssuanceTransactionWarning] = useState(false);

  const [verifierData, setVerifierData] = useState<SelectData>([]);

  const handleClosePanel = (): void => {
    onClose();
    setStandardSpecificIssuanceData(
      projectUnitMeasurement === "Biodiversity"
        ? ProjectIssuanceConstants.DEFAULT_BNG_ISSUANCE
        : ProjectIssuanceConstants.DEFAULT_CARBON_ISSUANCE
    );
    setTagPills([]);
    setChangedTagsExternal([]);
    setErrors([]);
    setShowIssuanceTransactionWarning(false);
  };

  const checkIssuanceTransactions = useCallback(
    async (uuid: string): Promise<boolean> => {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.issuanceUuid = {
        operator: "eq",
        value: uuid,
      };

      const res = await searchIssuanceTransactions({
        paging: {
          limit: 1,
          beforeCursor: null,
          afterCursor: null,
        },
        filter: { results: filterCriteria },
      });

      if (res.status === Status.Success && res.data?.results.length && res.data.results.length > 0) {
        setShowIssuanceTransactionWarning(true);
        return true;
      }
      return false;
    },
    [issuance?.uuid]
  );

  const handleSubmit = useIsLoadingWrapper(async (): Promise<void> => {
    setErrors([]);
    if (!projectDetails?.uuid) return;

    if (issuance?.uuid && !showIssuanceTransactionWarning) {
      if (await checkIssuanceTransactions(issuance?.uuid)) return;
    }

    const res =
      issuance === undefined
        ? await createProjectIssuance({
            projectUuid: projectDetails?.uuid,
            price: standardSpecificIssuanceData.price,
            priceCurrency: "GBP",
            status: standardSpecificIssuanceData.status ?? "",
            quantityType: standardSpecificIssuanceData.quantityType ?? "",
            issuanceDate: standardSpecificIssuanceData.issuanceDate,
            serialNumber: standardSpecificIssuanceData.serialNumber,
            availableQuantity: standardSpecificIssuanceData.availableQuantity ?? 0,
            reservedQuantity: standardSpecificIssuanceData.reservedQuantity ?? 0,
            soldQuantity: standardSpecificIssuanceData.soldQuantity ?? 0,
            totalQuantity:
              (standardSpecificIssuanceData.availableQuantity ?? 0) +
              (standardSpecificIssuanceData.reservedQuantity ?? 0) +
              (standardSpecificIssuanceData.soldQuantity ?? 0),
            standardSpecificIssuanceData: standardSpecificIssuanceData.standardSpecificIssuanceData,
            verifierUuid: standardSpecificIssuanceData.verifierUuid,
            validateOnly: false,
          })
        : await updateProjectIssuance({
            uuid: standardSpecificIssuanceData.uuid ?? "",
            price: standardSpecificIssuanceData.price,
            priceCurrency: "GBP",
            status: standardSpecificIssuanceData.status ?? "",
            quantityType: standardSpecificIssuanceData.quantityType ?? "",
            issuanceDate: standardSpecificIssuanceData.issuanceDate,
            serialNumber: standardSpecificIssuanceData.serialNumber,
            availableQuantity: standardSpecificIssuanceData.availableQuantity ?? 0,
            reservedQuantity: standardSpecificIssuanceData.reservedQuantity ?? 0,
            soldQuantity: standardSpecificIssuanceData.soldQuantity ?? 0,
            totalQuantity:
              (standardSpecificIssuanceData.availableQuantity ?? 0) +
              (standardSpecificIssuanceData.reservedQuantity ?? 0) +
              (standardSpecificIssuanceData.soldQuantity ?? 0),
            standardSpecificIssuanceData: standardSpecificIssuanceData.standardSpecificIssuanceData,
            rowVersion: standardSpecificIssuanceData.rowVersion ?? 0,
            verifierUuid: standardSpecificIssuanceData.verifierUuid,
          });

    if (res.status === Status.Success) {
      if (res.status === Status.Success && res.data !== undefined) {
        if (issuance === undefined && changedTagsExternal !== undefined && changedTagsExternal.length > 0) {
          await addOrRemoveObjectInteractions({
            objectInteractions:
              changedTagsExternal
                .filter((tag) => tag.checked)
                .map((tag) => ({
                  interactionUuid: tag.uuid,
                  objectType: InteractionObjectType.ISSUANCE,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  objectUuid: res.data!.uuid,
                  operation: "add",
                })) ?? [],
          });
        }
      }
      Toast.success({ message: "Issuance created successfully" });
      handleRefresh();
      handleClosePanel();
    }

    if (res.status === Status.Error) {
      setErrors(res.errors);
    }
  }, setIsHandleSubmitLoading);

  const handleSubmitForm = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    await handleSubmit();
  };

  const getAndSetIssuanceSchema = useCallback(
    async (uuid: string): Promise<void> => {
      const result = await getStandardSpecificSchemaDetails({ standardSpecificSchemaUuid: uuid });
      if (result.status === Status.Success && result.data) {
        try {
          const parsedSchema =
            typeof result.data.issuanceSchema === "string"
              ? (JSON.parse(result.data.issuanceSchema) as ProjectIssuanceSpecificSchema)
              : (result.data.issuanceSchema as ProjectIssuanceSpecificSchema);

          setIssuanceSchema({
            ...parsedSchema,
            properties: reorderIssuanceSchemaProperties(parsedSchema),
          });
        } catch {
          setIssuanceSchema(undefined);
        }
      }
    },
    [projectDetails?.standardSpecificSchemaUuid]
  );

  const fetchTags = useCallback(
    async (uuid: string, orgUuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.type = {
        operator: "eq",
        value: InteractionType.TAG,
      };
      filterCriteria.objectType = {
        operator: "eq",
        value: InteractionObjectType.ORGANISATION,
      };
      filterCriteria.objectUuid = {
        operator: "eq",
        value: orgUuid,
      };
      filterCriteria.objectInteractions = {
        some: {
          objectUuid: {
            operator: "eq",
            value: uuid,
          },
          endDate: {
            operator: "eq",
            value: null,
          },
        },
      };
      await searchInteractions({
        paging: {
          beforeCursor: null,
          afterCursor: null,
          limit: 50,
        },
        filter: { results: filterCriteria },
      }).then((response) => {
        if (response.data?.results != null) {
          setTagPills(
            response.data.results.map(
              (x) =>
                ({
                  key: x.uuid,
                  label: JSON.parse(x.data).name,
                }) as PillData
            )
          );
        }
      });
    },
    [issuance?.uuid, currentOrganisationUuid, refreshTags]
  );

  const getAndSetVerifiers = useCallback(
    async (uuid: string): Promise<void> => {
      const filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.projectTypes = {
        some: {
          uuid: {
            operator: "eq",
            value: uuid,
          },
        },
      };

      await searchVerifiers({
        filter: {
          results: filterCriteria,
        },
        paging: {
          limit: 10,
          beforeCursor: null,
          afterCursor: null,
        },
        sort: [],
      }).then((response) => {
        const valData =
          response.data?.results?.map((x) => {
            return { key: x.uuid, value: x.displayName };
          }) ?? [];
        setVerifierData(valData);
      });
    },
    [projectDetails?.projectType.uuid]
  );

  useEffect(() => {
    if (projectDetails?.standardSpecificSchemaUuid) {
      getAndSetIssuanceSchema(projectDetails.standardSpecificSchemaUuid);
    }
    if (projectDetails?.projectType.uuid) {
      getAndSetVerifiers(projectDetails.projectType.uuid);
    }
  }, [projectDetails?.standardSpecificSchemaUuid, projectDetails?.projectType.uuid]);

  useEffect(() => {
    setStandardSpecificIssuanceData(
      issuance ??
        (projectUnitMeasurement === "Biodiversity"
          ? ProjectIssuanceConstants.DEFAULT_BNG_ISSUANCE
          : ProjectIssuanceConstants.DEFAULT_CARBON_ISSUANCE)
    );
  }, [issuance?.uuid, projectUnitMeasurement]);

  useEffect(() => {
    if (issuance?.uuid && currentOrganisationUuid) {
      fetchTags(issuance.uuid, currentOrganisationUuid);
    } else {
      setTagPills(changedTagsExternal.filter((x) => x.checked).map((x) => ({ key: x.uuid, label: x.data.name })));
    }
  }, [issuance?.uuid, currentOrganisationUuid, refreshTags, changedTagsExternal]);

  return {
    errors,
    issuanceSchema,

    standardSpecificIssuanceData,
    setStandardSpecificIssuanceData,

    showTagsPanel,
    setShowTagsPanel,

    tagPills,
    changedTagsExternal,
    setChangedTagsExternal,

    setRefreshTags,

    isHandleSubmitLoading,
    handleClosePanel,
    handleSubmit,
    handleSubmitForm,
    showIssuanceTransactionWarning,
    setShowIssuanceTransactionWarning,
    verifierData,
  };
};
